import type { SidebarPage } from '../appSidebar/AppSidebar';
import { isDrop } from '../../constants/constants';
import type { Card } from '../../types/card';
import type { HubStatusTab } from '../hubChangesTable/hubRows';

/**
 * 저장 필터 칩 다섯(가격 목록 개편 §2-3 · PR 3, 결정 D7 — 고정 5개, 사용자 정의 보기는 안 한다, 감사 card-controltower#697).
 * 칩은 '조건' 이고 탭은 화면 안 이동이다. 칩마다 그 조건을 보는 화면(메뉴)이 정해져 있다 — 확정 대기 · 대상 아님은 PriceHub 변경
 * 목록(매장 가격 요청)의 상태 탭, 보류 · 하락 검토 · 동기화 실패는 카드 목록(가격 조회 표)의 조건.
 * 고른 칩은 주소의 `?view=` 에 남아 링크로 공유되고 새로고침해도 같은 보기로 연다.
 */
export type PriceView = 'pending' | 'held' | 'excluded' | 'drop' | 'syncFailed';

export const PRICE_VIEW_ORDER: PriceView[] = ['pending', 'held', 'excluded', 'drop', 'syncFailed'];

export const PRICE_VIEW_LABEL: Record<PriceView, string> = {
  // 용어 사전(규칙 41, 2026-11-04 개정) — CT 화면에서 '확정 대기' 는 쓰지 않는다(코덱 리뷰 #870).
  pending:    'PriceHub 확인 중',
  held:       '보류',
  excluded:   '대상 아님',
  drop:       '하락 검토',
  syncFailed: '동기화 실패',
};

/** 칩의 뜻 한 줄(title). */
export const PRICE_VIEW_HINT: Record<PriceView, string> = {
  pending:    'PriceHub 관리자가 아직 확인하지 않은 요청 · 판매가 결정 · 네이버에서 바뀐 값이 걸린 카드(매장 가격 요청)',
  held:       '하락 확인 대기이거나 사람이 정한 가격을 지키는 중인 카드(가격 조회)',
  excluded:   '반영도 요청도 할 수 없는 카드 — CT 에 없음 · 네이버 판매가 없음 · 고정 등(매장 가격 요청)',
  drop:       '가격이 내려가 검토할 카드(가격 조회)',
  syncFailed: '네이버 반영이 실패했거나 ③ 이 건너뛴 카드(가격 조회)',
};

/** 칩의 화면 — 그 조건을 보는 메뉴. */
export type PriceViewPage = Extract<SidebarPage, 'daily' | 'storePrice'>;
export const PRICE_VIEW_PAGE: Record<PriceView, PriceViewPage> = {
  pending:    'storePrice',
  excluded:   'storePrice',
  held:       'daily',
  drop:       'daily',
  syncFailed: 'daily',
};

/** 매장 가격 요청의 상태 탭 ↔ 칩(요청 가능은 칩 없음). */
export const hubTabOfView = (view: PriceView | null): HubStatusTab =>
  view === 'pending' ? 'pending' : view === 'excluded' ? 'blocked' : 'available';
export const viewOfHubTab = (tab: HubStatusTab): PriceView | null =>
  tab === 'pending' ? 'pending' : tab === 'blocked' ? 'excluded' : null;

export const PRICE_VIEW_PARAM = 'view';

export const isPriceView = (v: unknown): v is PriceView =>
  typeof v === 'string' && (PRICE_VIEW_ORDER as string[]).includes(v);

/** 주소의 `?view=` — 모르는 값 · 읽기 실패는 null. */
export function readPriceView(): PriceView | null {
  try {
    const v = new URLSearchParams(window.location.search).get(PRICE_VIEW_PARAM);
    return isPriceView(v) ? v : null;
  } catch {
    return null;
  }
}

/**
 * 주소의 `?view=` 를 바꾼다(다른 쿼리 · 해시는 그대로). 방문 기록은 늘리지 않는다 — 칩을 누를 때마다 뒤로가기가 쌓이지 않게.
 * 같은 값이면 아무것도 하지 않는다.
 */
export function writePriceView(view: PriceView | null): void {
  try {
    const url = new URL(window.location.href);
    const cur = url.searchParams.get(PRICE_VIEW_PARAM);
    if ((cur ?? null) === view) return;
    if (view) url.searchParams.set(PRICE_VIEW_PARAM, view);
    else url.searchParams.delete(PRICE_VIEW_PARAM);
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    /* 주소를 못 바꿔도 화면의 보기는 바뀐다 */
  }
}

/** 카드 목록 칩(보류 · 하락 검토 · 동기화 실패)의 조건. 확정 대기 · 대상 아님은 PriceHub 변경 줄의 상태라 여기서는 false. */
export function cardMatchesView(view: PriceView, c: Card, pendingConfirmIds: ReadonlySet<number>): boolean {
  switch (view) {
    case 'held':       return c.priceHeld === true || pendingConfirmIds.has(c.id);
    case 'drop':       return isDrop(c.priceChangeStatus);
    case 'syncFailed': return !!c.naverError;
    default:           return false;
  }
}

/** 칩 건수 — 필터 전 전체(결정 D8 — 지금 응답으로 화면에서 센다). null 은 '이 화면에서 모름'(PriceHub 변경 목록을 받지 않은 화면). */
export type PriceViewCounts = Record<PriceView, number | null>;

/** 카드 목록 칩 셋의 건수 — `cards` 는 표 한 장의 전체(가격 조회 네 탭의 카드)를 넘긴다. */
export function countCardViews(
  cards: Card[], pendingConfirmIds: ReadonlySet<number>,
): Pick<PriceViewCounts, 'held' | 'drop' | 'syncFailed'> {
  let held = 0; let drop = 0; let syncFailed = 0;
  for (const c of cards) {
    if (cardMatchesView('held', c, pendingConfirmIds)) held += 1;
    if (cardMatchesView('drop', c, pendingConfirmIds)) drop += 1;
    if (cardMatchesView('syncFailed', c, pendingConfirmIds)) syncFailed += 1;
  }
  return { held, drop, syncFailed };
}

// ─── 신선도(결정 D10 — 6시간) ─────────────────────────────────────────────────

/** 이 시간이 지나면 '오래됨'(05:00 자동 수집 · 08:00 PriceHub 기준이면 오후엔 이미 하루치가 아니다). */
export const STALE_AFTER_MS = 6 * 60 * 60 * 1000;

/**
 * 마지막 가격 수집 시각 — PriceHub 에서 가격을 받아 수정가를 갱신한 시각(`priceUpdatedAt`, 백엔드 Card.applyFetchedPrice) 중 가장
 * 늦은 것. 사람이 넣은 가격(isManual)은 수집이 아니라 뺀다. 없으면 null.
 */
export function lastCollectedAt(cards: Card[]): Date | null {
  let best: number | null = null;
  for (const c of cards) {
    if (c.isManual || !c.priceUpdatedAt) continue;
    const ms = Date.parse(c.priceUpdatedAt);
    if (Number.isFinite(ms) && (best == null || ms > best)) best = ms;
  }
  return best == null ? null : new Date(best);
}

export const isStale = (at: Date | null, now: number): boolean => at != null && now - at.getTime() > STALE_AFTER_MS;
